import React from "react";
import { useTable, useRowSelect } from "react-table";
import "../src/DataTable.css";

const DataTable = ({ data, isDarkMode = false }) => {
  // Ensure data is an array
  const safeData = Array.isArray(data) ? data : [];

  // Create empty columns array for when data is empty
  const columns = React.useMemo(() => {
    if (safeData.length === 0) return [];
    // Ensure we're working with a proper object
    const firstRow = typeof safeData[0] === "object" ? safeData[0] : {};
    return Object.keys(firstRow).map((key) => ({
      Header: key,
      accessor: key,
      Cell: ({ value }) => {
        // Handle different value types
        if (value === null || value === undefined) return "-";
        if (typeof value === "object") return JSON.stringify(value);
        return String(value);
      },
    }));
  }, [safeData]);

  // Always call useTable hook, even with empty data
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({
      columns,
      data: safeData,
    });

  // Handle empty data case after hooks are called
  if (safeData.length === 0) {
    return (
      <div className="table-container overflow-hidden">
        <div
          className={`flex flex-col items-center justify-center p-3 ${
            isDarkMode
              ? "bg-gradient-to-b from-gray-800 to-gray-700"
              : "bg-gradient-to-b from-gray-50 to-white"
          } rounded-lg border ${
            isDarkMode ? "border-gray-600" : "border-gray-100/80"
          } mx-auto`}
        >
          <div
            className={`${
              isDarkMode
                ? "bg-blue-900/80 border-blue-700/50"
                : "bg-blue-50/80 border-blue-100/50"
            } rounded-full p-2 mb-2 border shadow-inner group-hover:shadow-md group-hover:scale-105 transition-all duration-300`}
          >
            <svg
              className={`w-6 h-6 ${
                isDarkMode ? "text-blue-400" : "text-blue-500"
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
                d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
              />
            </svg>
          </div>

          <h3
            className={`text-sm font-medium ${
              isDarkMode ? "text-gray-200" : "text-gray-800"
            } mb-1`}
          >
            Ready to Write Your SQL Query
          </h3>
          <p
            className={`${
              isDarkMode ? "text-gray-400" : "text-gray-600"
            } text-center mb-2 text-xs max-w-md`}
          >
            Follow these steps to solve the challenge:
          </p>

          <div className="w-full space-y-1.5">
            <div
              className={`flex items-start gap-2 p-1.5 ${
                isDarkMode
                  ? "bg-gray-700 border-gray-600 hover:border-blue-600"
                  : "bg-white border-gray-200 hover:border-blue-200"
              } rounded-md border transition-colors group`}
            >
              <div
                className={`flex-shrink-0 w-5 h-5 rounded-full ${
                  isDarkMode
                    ? "bg-blue-900 text-blue-300"
                    : "bg-blue-100 text-blue-600"
                } flex items-center justify-center font-medium text-xs`}
              >
                1
              </div>
              <div>
                <h4
                  className={`font-medium text-xs ${
                    isDarkMode
                      ? "text-gray-200 group-hover:text-blue-400"
                      : "text-gray-800 group-hover:text-blue-600"
                  } transition-colors`}
                >
                  Write Your Query
                </h4>
                <p
                  className={`${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  } text-[10px]`}
                >
                  Use the above SQL Playground to write your query
                </p>
              </div>
            </div>

            <div
              className={`flex items-start gap-2 p-1.5 ${
                isDarkMode
                  ? "bg-gray-700 border-gray-600 hover:border-green-600"
                  : "bg-white border-gray-200 hover:border-green-200"
              } rounded-md border transition-colors group`}
            >
              <div
                className={`flex-shrink-0 w-5 h-5 rounded-full ${
                  isDarkMode
                    ? "bg-green-900 text-green-300"
                    : "bg-green-100 text-green-600"
                } flex items-center justify-center font-medium text-xs`}
              >
                2
              </div>
              <div>
                <h4
                  className={`font-medium text-xs ${
                    isDarkMode
                      ? "text-gray-200 group-hover:text-green-400"
                      : "text-gray-800 group-hover:text-green-600"
                  } transition-colors`}
                >
                  Execute Your Query
                </h4>
                <p
                  className={`${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  } text-[10px]`}
                >
                  Click
                  <span
                    className={`inline-flex items-center px-1 py-0.5 ${
                      isDarkMode
                        ? "bg-gray-600 text-gray-300"
                        : "bg-gray-100 text-gray-700"
                    } text-[10px] rounded mx-1`}
                  >
                    Execute
                    <svg
                      className="w-2 h-2 ml-0.5"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                    >
                      <path
                        d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  to see results
                </p>
              </div>
            </div>

            <div
              className={`flex items-start gap-2 p-1.5 ${
                isDarkMode
                  ? "bg-gray-700 border-gray-600 hover:border-indigo-600"
                  : "bg-white border-gray-200 hover:border-indigo-200"
              } rounded-md border transition-colors group`}
            >
              <div
                className={`flex-shrink-0 w-5 h-5 rounded-full ${
                  isDarkMode
                    ? "bg-indigo-900 text-indigo-300"
                    : "bg-indigo-100 text-indigo-600"
                } flex items-center justify-center font-medium text-xs`}
              >
                3
              </div>
              <div>
                <h4
                  className={`font-medium text-xs ${
                    isDarkMode
                      ? "text-gray-200 group-hover:text-indigo-400"
                      : "text-gray-800 group-hover:text-indigo-600"
                  } transition-colors`}
                >
                  Submit to Check
                </h4>
                <p
                  className={`${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  } text-[10px]`}
                >
                  Click
                  <span
                    className={`inline-flex items-center px-1 py-0.5 ${
                      isDarkMode
                        ? "bg-blue-900 text-blue-300"
                        : "bg-blue-100 text-blue-700"
                    } text-[10px] rounded mx-1`}
                  >
                    Submit
                    <svg
                      className="w-2 h-2 ml-0.5"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                    >
                      <path
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  to check your answer
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`table-container ${isDarkMode ? "dark" : ""}`}>
      <table
        {...getTableProps()}
        className={`table ${isDarkMode ? "dark" : ""}`}
      >
        <thead>
          {headerGroups.map((headerGroup, groupIndex) => (
            <tr
              {...headerGroup.getHeaderGroupProps()}
              key={`header-group-${groupIndex}`}
            >
              {headerGroup.headers.map((column, colIndex) => (
                <th {...column.getHeaderProps()} key={`header-${colIndex}`}>
                  {column.render("Header")}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row, rowIndex) => {
            prepareRow(row);
            return (
              <tr
                {...row.getRowProps()}
                key={`row-${rowIndex}`}
                className={rowIndex % 2 === 0 ? "even-row" : "odd-row"}
              >
                {row.cells.map((cell, cellIndex) => (
                  <td {...cell.getCellProps()} key={`cell-${cellIndex}`}>
                    {cell.render("Cell")}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default DataTable;
